import { PriceType } from '@cfixe-monorepo/shared';
import type { CartItem } from '../types';

export function hasNonZeroFormattedAmount(value: string | undefined): boolean {
  if (!value) return false;
  const numericPart = value.replace(/[^\d,.-]/g, '');
  return /[1-9]/.test(numericPart);
}

/**
 * True when any cart item is priced "on quote" (devis) or "from" (à partir de) —
 * meaning every displayed amount is only a lower bound, not the final price.
 */
export function cartHasQuoteOrFromPricing(cartItems: CartItem[]): boolean {
  return cartItems.some(
    (item): boolean =>
      item.selectedPrice.price_type === PriceType.QUOTE || item.selectedPrice.price_type === PriceType.FROM,
  );
}

/**
 * True when any cart item has no set price (PriceType.NONE) — the amount will be
 * determined (and settled) on site, so a "rien à régler sur place" message would be misleading.
 */
export function cartHasNoPricePricing(cartItems: CartItem[]): boolean {
  return cartItems.some((item): boolean => item.selectedPrice.price_type === PriceType.NONE);
}

/**
 * True when a displayed amount is only a lower bound, not the final price — i.e. the cart mixes
 * in any "non-firm" pricing: QUOTE/FROM (à partir de / sur devis) OR NONE (prix à déterminer).
 * Drives the "à partir de X" prefix so a known sub-total is never read as the full price.
 */
export function cartHasLowerBoundPricing(cartItems: CartItem[]): boolean {
  return cartHasQuoteOrFromPricing(cartItems) || cartHasNoPricePricing(cartItems);
}

/**
 * Prefixes a formatted amount with the "à partir de" wording when the amount is only
 * a lower bound (quote/from pricing), so the user never reads an estimate as a firm price.
 */
export function withFromPrefix(amount: string, isQuoteOrFrom: boolean, fromPrefix: string): string {
  return isQuoteOrFrom ? `${fromPrefix} ${amount}` : amount;
}

/**
 * Formats a summary-level amount (total, remaining-on-site) whose row is always displayed,
 * honoring quote/from pricing so an estimate is never shown as a firm price:
 *  - not quote/from → the amount as-is;
 *  - quote/from with a known non-zero floor → "<fromPrefix> <amount>" (e.g. "à partir de 50 €");
 *  - quote/from with no firm amount → the "on quote" wording.
 * Centralizes the branching so the manage view no longer reimplements it inline.
 */
export function formatFlexibleAmount(
  amount: string | undefined,
  isQuoteOrFrom: boolean,
  labels: { fromPrefix: string; onQuote: string },
): string | undefined {
  if (!isQuoteOrFrom) return amount;
  return hasNonZeroFormattedAmount(amount) ? `${labels.fromPrefix} ${amount}` : labels.onQuote;
}

/**
 * True when the cart will trigger an online payment step — drives the "Paiement sécurisé" trust
 * bar, so a free discovery call never advertises card logos. Mirrors the backend pre-reservation:
 * a line is chargeable online only if its prestation is not in FREE mode (`requires_online_payment`,
 * resolved server-side; absent ⇒ payable) AND it has an amount — a 0 € cart takes the zero-amount
 * path without Stripe, CARD_SAVE / BANK_IMPRINT included. Raw minor-unit amounts are only compared
 * to 0 here, never formatted.
 */
export function cartRequiresOnlinePayment(cartItems: CartItem[]): boolean {
  return cartItems.some(
    (item): boolean =>
      item.prestation.requires_online_payment !== false &&
      (item.selectedPrice.price > 0 || item.supplements.some((s): boolean => s.selected && (s.price ?? 0) > 0)),
  );
}
